import { SEARCH_MODE, USER_EVENTS } from "@constants/userEvent";
import { IonButton, IonCol, IonIcon, IonLabel, IonRow, IonSpinner } from "@ionic/react";
import { useSession } from "@src/app/store/helperHooks";
import { useOpenShiftItem } from "@src/app/store/helperHooks/shiftStoreHelper";
import { OpenShiftItemBookingStatus } from "@src/app/store/shift/model";
import { AgentStage } from "@src/appV2/Agents";
import { logEvent } from "@src/appV2/lib/analytics";
import { Shift } from "@src/lib/interface";
import { alarmOutline } from "ionicons/icons";
import pluralize from "pluralize";
import { useCallback, useMemo } from "react";

import { checkRolloutMsa } from "../../utils/shiftHelper";
import { NonQfaShiftItemLabels } from "../constants";
import { useMissingDocuments } from "../custom-hooks/useMissingDocuments";
import { ShiftItemCategory } from "../model";

interface ShiftActionButtonRowProps {
  shift: Shift;
  onClick: () => any;
  category: ShiftItemCategory;
  submitting: any;
  documentsAreMissing: boolean;
  isInterested: boolean;
  isCheckingDocuments: boolean;
  isPriorityShift: boolean;
}

export function ShiftActionButtonRow(props: ShiftActionButtonRowProps) {
  const {
    shift,
    onClick,
    category,
    submitting,
    documentsAreMissing,
    isInterested,
    isCheckingDocuments,
    isPriorityShift,
  } = props;
  const { agent = {}, bookingRolloutMsas } = useSession();
  const { missingDocuments, pendingDocuments } = useMissingDocuments(shift);

  const openShiftItem = useOpenShiftItem(shift._id ?? "") ?? {};

  const agentIsEmptyObject = useMemo(() => {
    return Object.keys(agent).length === 0 && agent.constructor === Object;
  }, [agent]);

  const onChatClick = useCallback(() => {
    logEvent(USER_EVENTS.CHAT_STARTED, {
      hcpId: agent.userId,
      hcpName: `${agent.firstName} ${agent.lastName}`,
      shiftId: shift._id,
      shiftStartTime: shift.start,
      shiftGroupId: shift.groupId ? shift.groupId : "empty",
      shiftEndTime: shift.end,
      hcpType: agent.preference?.qualification,
      facilityId: shift.facility && shift.facility._id,
      facilityName: shift.facility && shift.facility.name,
    });
  }, [agent, shift]);

  const checkDocuments = useCallback(() => {
    const props: {
      isMissing: boolean;
      fill: "solid" | "outline";
      label: string;
    } = {
      isMissing: false,
      fill: "solid",
      label: "",
    };

    if (
      [
        ShiftItemCategory.MISSING_CBH_DOCUMENTS,
        ShiftItemCategory.STRIPE_NOT_CONNECTED_AND_MISSING_HCP_DOCUMENTS,
      ].indexOf(category) !== -1
    ) {
      props.fill = "outline";
      props.label = `Upload Required ${pluralize("Document", missingDocuments.size)}`;
      props.isMissing = true;
    }
    if (category === ShiftItemCategory.WAITING_FOR_APPROVE) {
      props.fill = "outline";
      props.label = `View Pending ${pluralize("Document", pendingDocuments.size)}`;
      props.isMissing = true;
    }

    return props;
  }, [category, missingDocuments, pendingDocuments]);

  /**
   * FIXME
   * This is an insane set of code to determine buttonProps, impossible to really debug.
   * It certainly shouldn't be memoized either.
   * Refactor to be inline, or extract a component if it really is so special
   * When no content present avoid showing the button.
   */
  const buttonProps = useMemo(() => {
    let defaultProps: {
      onClick: typeof onClick;
      color: string;
      label: string | null;
      fill: "solid" | "outline" | "clear" | "default" | undefined;
      disabled: boolean;
    } = {
      onClick,
      color: isPriorityShift ? "shift-priority" : "primary",
      label: null,
      fill: undefined,
      disabled: false,
    };

    if (agent.stage === AgentStage.DEACTIVATED) {
      defaultProps.color = "secondary";
      defaultProps.onClick = onChatClick;
      defaultProps.label = `Your account is ${agent.stage.toLowerCase()}. Click here to chat`;

      return defaultProps;
    }
    const { isMissing, ...missingDocsProps } = checkDocuments();

    if (isCheckingDocuments) {
      defaultProps.label = "Checking Docs";
      defaultProps.fill = "outline";
      defaultProps.disabled = true;
      return defaultProps;
    }

    if ((agent.isTestAccount ?? false) !== (shift.facility?.isTestAccount ?? false)) {
      defaultProps.label = "Disabled (Test Account)";
      defaultProps.color = "secondary";
      defaultProps.fill = "outline";
      defaultProps.disabled = true;
      return defaultProps;
    }

    if (openShiftItem?.types?.includes(SEARCH_MODE.NON_QFA_RANGE_SHIFTS)) {
      const bookingStatus = openShiftItem?.bookingStatus;
      switch (bookingStatus) {
        case OpenShiftItemBookingStatus.NON_QFA_RANGE_SHIFT_CHECKING_DOCUMENT_STATUS:
          defaultProps.label = NonQfaShiftItemLabels.CHECKING_DOCUMENT_STATUS;
          defaultProps.fill = "clear";
          break;
        case OpenShiftItemBookingStatus.NON_QFA_RANGE_SHIFT_DOCUMENT_STATUS_PASS:
          defaultProps.label = NonQfaShiftItemLabels.DOCUMENT_STATUS_CHECK_PASS;
          defaultProps.fill = "solid";
          break;
        case OpenShiftItemBookingStatus.NON_QFA_RANGE_SHIFT_DOCUMENT_STATUS_FAIL:
          defaultProps.label = NonQfaShiftItemLabels.DOCUMENT_STATUS_CHECK_FAIL;
          defaultProps.fill = "outline";
          break;
        default:
          defaultProps.label = NonQfaShiftItemLabels.CHECK_DOCUMENT_STATUS;
          defaultProps.fill = "solid";
      }
      return defaultProps;
    }

    if (checkRolloutMsa(shift, bookingRolloutMsas as string[])) {
      if (!isMissing) {
        defaultProps.label = "Book This Shift";
        defaultProps.fill = "solid";
        return defaultProps;
      }

      defaultProps = { ...defaultProps, ...missingDocsProps };
    } else {
      if (documentsAreMissing || isInterested) {
        defaultProps = { ...defaultProps, ...missingDocsProps };
      } else {
        defaultProps.label = "Book This Shift";
        defaultProps.fill = "solid";
      }
    }

    return defaultProps;
  }, [
    shift,
    agent.stage,
    agent.isTestAccount,
    isInterested,
    documentsAreMissing,
    onChatClick,
    onClick,
    checkDocuments,
    bookingRolloutMsas,
    isCheckingDocuments,
    isPriorityShift,
    openShiftItem?.types,
    openShiftItem?.bookingStatus,
  ]);

  if (!buttonProps.label) {
    return null;
  }
  if (!agent || agentIsEmptyObject) {
    return null;
  }

  return (
    <IonRow>
      <IonCol className="ion-text-center">
        <IonButton
          {...buttonProps}
          shape="round"
          strong={true}
          className="shift-ion-button ion-button"
          disabled={buttonProps.disabled || submitting}
        >
          {!submitting && !isCheckingDocuments && isPriorityShift && (
            <IonIcon slot="start" icon={alarmOutline}></IonIcon>
          )}
          {isCheckingDocuments && <IonSpinner name="circles" />}
          <IonLabel>{submitting ? <IonSpinner name="dots" /> : buttonProps.label}</IonLabel>
        </IonButton>
      </IonCol>
    </IonRow>
  );
}
